import Swiper from 'swiper';
import { A11y, Navigation, Pagination } from 'swiper/modules';

const productSetRoot = document.querySelector('[data-product-set-root]');

if (productSetRoot) {
    const overlay = productSetRoot.querySelector('[data-product-set-overlay]');
    const drawer = productSetRoot.querySelector('[data-product-set-drawer]');
    const sourceProductId = productSetRoot.dataset.sourceProductId;
    const nonce = productSetRoot.dataset.productSetNonce;
    const getAction = productSetRoot.dataset.productSetGetAction;
    const addAction = productSetRoot.dataset.productSetAddAction;
    const addedChoices = new Set();
    let isLoaded = false;
    let loadingPromise = null;
    let lastFocusedElement = null;
    let pendingFocusProductId = '';
    let previousOverflow = '';
    let closeTimer = null;
    let sizeGuideTimer = null;
    let lastSizeGuideTrigger = null;

    const getOpenTriggers = () => Array.from(document.querySelectorAll('[data-product-set-open]'));

    const getFocusableElements = () =>
        Array.from(
            overlay.querySelectorAll(
                'a[href], button:not([disabled]), select:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
            ),
        ).filter(
            (element) =>
                !element.hidden && element.offsetParent !== null && !element.closest('[inert]'),
        );

    const setStatus = (item, message, type = '') => {
        const status = item.querySelector('[data-product-set-item-status]');

        if (!status) {
            return;
        }

        status.textContent = message;
        status.classList.toggle('is-error', type === 'error');
        status.classList.toggle('is-success', type === 'success');
    };

    const getChoiceKey = (item) => {
        const productId = item.dataset.productId || '';
        const variationId = item.dataset.selectedVariationId || '0';

        return `${productId}:${variationId}`;
    };

    const updateAddButton = (item) => {
        const button = item.querySelector('[data-product-set-add]');
        const label = button?.querySelector('[data-product-set-add-label]');

        if (!button || !label || item.classList.contains('is-unavailable')) {
            return;
        }

        const requiresVariation = item.dataset.requiresVariation === 'true';
        const hasVariation = Boolean(item.dataset.selectedVariationId);
        const alreadyAdded = addedChoices.has(getChoiceKey(item));

        button.disabled = (requiresVariation && !hasVariation) || alreadyAdded;
        item.classList.toggle('is-added', alreadyAdded);

        if (alreadyAdded) {
            label.textContent = 'DODANO ✓';
        } else if (requiresVariation && !hasVariation) {
            label.textContent = (item.dataset.selectionLabel || 'Wybierz wariant').toUpperCase();
        } else {
            label.textContent = 'DODAJ DO KOSZYKA';
        }
    };

    const getSizeGuidePanel = () => drawer?.querySelector('[data-product-set-size-guide-panel]');

    const setPrimaryDrawerContentInert = (isInert, panel) => {
        Array.from(drawer.children).forEach((child) => {
            if (child !== panel) {
                child.inert = isInert;
            }
        });
    };

    const closeSizeGuide = ({ restoreFocus = true, immediate = false } = {}) => {
        const panel = getSizeGuidePanel();

        if (!panel || panel.hidden) {
            return false;
        }

        const content = panel.querySelector('[data-product-set-size-guide-content]');
        panel.classList.remove('is-open');
        panel.setAttribute('aria-hidden', 'true');
        drawer.classList.remove('is-size-guide-open');
        setPrimaryDrawerContentInert(false, panel);
        drawer
            .querySelectorAll('[data-product-set-size-guide]')
            .forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));

        if (sizeGuideTimer) {
            window.clearTimeout(sizeGuideTimer);
            sizeGuideTimer = null;
        }

        const finishClose = () => {
            panel.hidden = true;
            content?.replaceChildren();
            sizeGuideTimer = null;
        };

        if (immediate) {
            finishClose();
        } else {
            sizeGuideTimer = window.setTimeout(finishClose, 240);
        }

        if (restoreFocus && lastSizeGuideTrigger?.isConnected) {
            lastSizeGuideTrigger.focus({ preventScroll: true });
        }

        lastSizeGuideTrigger = null;
        return true;
    };

    const openSizeGuide = (trigger) => {
        const item = trigger.closest('[data-product-set-item]');
        const template = item?.querySelector('[data-product-set-size-guide-template]');
        const panel = getSizeGuidePanel();
        const content = panel?.querySelector('[data-product-set-size-guide-content]');
        const title = panel?.querySelector('[data-product-set-size-guide-title]');

        if (!template || !panel || !content || !title) {
            return;
        }

        if (sizeGuideTimer) {
            window.clearTimeout(sizeGuideTimer);
            sizeGuideTimer = null;
        }

        lastSizeGuideTrigger = trigger;
        content.replaceChildren(template.content.cloneNode(true));
        title.textContent = trigger.dataset.productName || '';
        panel.hidden = false;
        panel.setAttribute('aria-hidden', 'false');
        drawer.classList.add('is-size-guide-open');
        setPrimaryDrawerContentInert(true, panel);
        trigger.setAttribute('aria-expanded', 'true');

        window.requestAnimationFrame(() => {
            panel.classList.add('is-open');
            panel.querySelector('[data-product-set-size-guide-close]')?.focus({
                preventScroll: true,
            });
        });
    };

    const selectVariation = (item, variationId, attributes, priceHtml, control = null) => {
        item.dataset.selectedVariationId = String(variationId || '');
        item.dataset.selectedAttributes = attributes || '';

        item.querySelectorAll('[data-variation-id]').forEach((choice) => {
            const isSelected = choice === control;
            choice.classList.toggle('is-selected', isSelected);
            choice.setAttribute('aria-checked', isSelected ? 'true' : 'false');
        });

        const price = item.querySelector('[data-product-set-item-price]');
        if (price && priceHtml) {
            price.innerHTML = priceHtml;
        }

        setStatus(item, '');
        updateAddButton(item);
    };

    const selectCurrentProductVariation = () => {
        const variationInput = document.querySelector('form.variations_form input[name="variation_id"]');
        const selectedVariationId = variationInput ? String(variationInput.value || '') : '';

        if (!selectedVariationId || !drawer) {
            return;
        }

        const sourceItem = drawer.querySelector(
            `[data-product-set-item][data-product-id="${sourceProductId}"]`,
        );
        const matchingChoice = sourceItem
            ? Array.from(sourceItem.querySelectorAll('[data-variation-id]')).find(
                  (choice) => choice.dataset.variationId === selectedVariationId && !choice.disabled,
              )
            : null;

        if (matchingChoice) {
            selectVariation(
                sourceItem,
                matchingChoice.dataset.variationId,
                matchingChoice.dataset.attributes,
                matchingChoice.dataset.priceHtml,
                matchingChoice,
            );
        }
    };

    const focusRequestedProduct = () => {
        if (!pendingFocusProductId || !drawer) {
            return;
        }

        const item = drawer.querySelector(
            `[data-product-set-item][data-product-id="${pendingFocusProductId}"]`,
        );

        if (item) {
            item.scrollIntoView({
                behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
                block: 'center',
            });
            item.classList.add('is-highlighted');
            window.setTimeout(() => item.classList.remove('is-highlighted'), 1200);
        }

        pendingFocusProductId = '';
    };

    const renderLoadError = (message) => {
        drawer.innerHTML = '';

        const header = document.createElement('header');
        header.className = 'ProductSetDrawer__header';
        const title = document.createElement('strong');
        title.textContent = 'STWÓRZ SET';
        const close = document.createElement('button');
        close.className = 'Button--no-style ProductSetDrawer__close';
        close.type = 'button';
        close.dataset.productSetClose = '';
        close.setAttribute('aria-label', 'Zamknij konfigurator setu');
        close.innerHTML =
            '<span>Zamknij</span><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" aria-hidden="true"><path fill="currentColor" d="M1.423.308 7.077 5.962 12.73.308a.789.789 0 0 1 1.116 1.116L8.192 7.077l5.654 5.654a.789.789 0 0 1-1.116 1.115L7.077 8.192l-5.654 5.654a.789.789 0 0 1-1.115-1.115l5.654-5.654L.308 1.424A.789.789 0 0 1 1.423.308Z"/></svg>';
        header.append(title, close);

        const error = document.createElement('div');
        error.className = 'ProductSetDrawer__error';
        const errorTitle = document.createElement('strong');
        errorTitle.textContent = 'Nie udało się pobrać setu';
        const errorMessage = document.createElement('p');
        errorMessage.textContent = message || 'Spróbuj ponownie za chwilę.';
        const retry = document.createElement('button');
        retry.className = 'Button--no-style ProductSetDrawer__retry';
        retry.type = 'button';
        retry.dataset.productSetRetry = '';
        retry.textContent = 'SPRÓBUJ PONOWNIE';
        error.append(errorTitle, errorMessage, retry);
        drawer.append(header, error);
    };

    const loadDrawer = () => {
        if (isLoaded) {
            return Promise.resolve();
        }

        if (loadingPromise) {
            return loadingPromise;
        }

        const requestData = new URLSearchParams({
            action: getAction,
            source_product_id: sourceProductId,
            nonce,
        });

        drawer.classList.add('is-loading');
        loadingPromise = fetch(window.jsData.ajaxUrl, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
            },
            body: requestData.toString(),
        })
            .then((response) => response.json())
            .then((response) => {
                if (!response.success || !response.data?.html) {
                    throw new Error(response.data?.message || 'Nie udało się pobrać produktów.');
                }

                drawer.innerHTML = response.data.html;
                isLoaded = true;
                selectCurrentProductVariation();
                focusRequestedProduct();
            })
            .catch((error) => {
                renderLoadError(error.message);
                throw error;
            })
            .finally(() => {
                drawer.classList.remove('is-loading');
                loadingPromise = null;
            });

        return loadingPromise;
    };

    const focusDrawer = () => {
        const closeButton = drawer.querySelector('[data-product-set-close]');
        (closeButton || overlay).focus({ preventScroll: true });
    };

    const openDrawer = (trigger) => {
        if (!overlay || !drawer) {
            return;
        }

        pendingFocusProductId = trigger?.dataset.productSetFocusProduct || '';
        lastFocusedElement = document.activeElement;
        previousOverflow = document.documentElement.style.overflow;
        if (closeTimer) {
            window.clearTimeout(closeTimer);
            closeTimer = null;
        }
        overlay.hidden = false;
        overlay.setAttribute('aria-hidden', 'false');
        getOpenTriggers().forEach((openTrigger) => openTrigger.setAttribute('aria-expanded', 'true'));
        document.documentElement.style.overflow = 'hidden';
        document.documentElement.classList.add('ProductSet-is-open');

        window.requestAnimationFrame(() => {
            overlay.classList.add('is-open');
        });

        focusDrawer();
        loadDrawer()
            .then(() => {
                focusDrawer();
                focusRequestedProduct();
            })
            .catch(() => {
                focusDrawer();
            });
    };

    const closeDrawer = () => {
        if (!overlay || overlay.hidden) {
            return;
        }

        closeSizeGuide({ restoreFocus: false, immediate: true });
        overlay.classList.remove('is-open');
        overlay.setAttribute('aria-hidden', 'true');
        getOpenTriggers().forEach((openTrigger) => openTrigger.setAttribute('aria-expanded', 'false'));
        document.documentElement.style.overflow = previousOverflow;
        document.documentElement.classList.remove('ProductSet-is-open');

        closeTimer = window.setTimeout(() => {
            if (overlay.classList.contains('is-open')) {
                return;
            }

            overlay.hidden = true;
            closeTimer = null;
        }, 260);

        if (lastFocusedElement && lastFocusedElement.isConnected) {
            lastFocusedElement.focus({ preventScroll: true });
        }
    };

    const retryLoad = () => {
        isLoaded = false;
        drawer.innerHTML = `
            <header class="ProductSetDrawer__header">
                <strong>STWÓRZ SET</strong>
                <button class="Button--no-style ProductSetDrawer__close" type="button" aria-label="Zamknij konfigurator setu" data-product-set-close>
                    <span>Zamknij</span>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" aria-hidden="true"><path fill="currentColor" d="M1.423.308 7.077 5.962 12.73.308a.789.789 0 0 1 1.116 1.116L8.192 7.077l5.654 5.654a.789.789 0 0 1-1.116 1.115L7.077 8.192l-5.654 5.654a.789.789 0 0 1-1.115-1.115l5.654-5.654L.308 1.424A.789.789 0 0 1 1.423.308Z"/></svg>
                </button>
            </header>
            <div class="ProductSetDrawer__loading" role="status">
                <span class="ProductSetDrawer__spinner" aria-hidden="true"></span>
                <span>Pobieramy aktualne warianty i dostępność…</span>
            </div>
        `;
        loadDrawer().then(focusDrawer).catch(focusDrawer);
    };

    const addProduct = (button) => {
        const item = button.closest('[data-product-set-item]');

        if (!item || button.disabled || button.classList.contains('is-loading')) {
            return;
        }

        const label = button.querySelector('[data-product-set-add-label]');
        const variationId = item.dataset.selectedVariationId || '0';
        const attributes = item.dataset.selectedAttributes || '{}';
        const requestData = new URLSearchParams({
            action: addAction,
            source_product_id: sourceProductId,
            product_id: item.dataset.productId,
            variation_id: variationId,
            attributes,
            nonce,
        });

        button.classList.add('is-loading');
        button.disabled = true;
        label.textContent = 'DODAJEMY…';
        setStatus(item, '');

        fetch(window.jsData.ajaxUrl, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
            },
            body: requestData.toString(),
        })
            .then((response) => response.json())
            .then((response) => {
                if (!response.success) {
                    throw new Error(response.data?.message || 'Nie udało się dodać produktu.');
                }

                addedChoices.add(getChoiceKey(item));
                item.classList.add('is-added');
                setStatus(item, response.data.message || 'Produkt dodano do koszyka.', 'success');
                updateAddButton(item);

                if (window.jQuery) {
                    const $button = window.jQuery(button);
                    window.jQuery(document.body).trigger('added_to_cart', [
                        {},
                        response.data.cart_hash || '',
                        $button,
                    ]);
                    window.jQuery(document.body).trigger('wc_fragment_refresh');
                }

                const removeGeneratedCartLink = () => {
                    item.querySelectorAll('.added_to_cart.wc-forward').forEach((link) => link.remove());
                    button.classList.remove('added');
                };

                removeGeneratedCartLink();
                window.setTimeout(removeGeneratedCartLink, 0);
            })
            .catch((error) => {
                setStatus(item, error.message, 'error');
                button.disabled = false;
                label.textContent = 'SPRÓBUJ PONOWNIE';
            })
            .finally(() => {
                button.classList.remove('is-loading');
            });
    };

    document.addEventListener('click', (event) => {
        const openTrigger = event.target.closest('[data-product-set-open]');
        if (openTrigger) {
            event.preventDefault();
            openDrawer(openTrigger);
            return;
        }

        if (!overlay || overlay.hidden) {
            return;
        }

        const closeTrigger = event.target.closest('[data-product-set-close]');
        if (closeTrigger || event.target === overlay) {
            event.preventDefault();
            closeDrawer();
            return;
        }

        const sizeGuideClose = event.target.closest('[data-product-set-size-guide-close]');
        if (sizeGuideClose) {
            event.preventDefault();
            closeSizeGuide();
            return;
        }

        const sizeGuideTrigger = event.target.closest('[data-product-set-size-guide]');
        if (sizeGuideTrigger) {
            event.preventDefault();
            openSizeGuide(sizeGuideTrigger);
            return;
        }

        const retryTrigger = event.target.closest('[data-product-set-retry]');
        if (retryTrigger) {
            event.preventDefault();
            retryLoad();
            return;
        }

        const variationChoice = event.target.closest('[data-variation-id]');
        if (variationChoice && !variationChoice.disabled) {
            const item = variationChoice.closest('[data-product-set-item]');
            selectVariation(
                item,
                variationChoice.dataset.variationId,
                variationChoice.dataset.attributes,
                variationChoice.dataset.priceHtml,
                variationChoice,
            );
            return;
        }

        const addButton = event.target.closest('[data-product-set-add]');
        if (addButton) {
            event.preventDefault();
            addProduct(addButton);
        }
    });

    overlay.addEventListener('change', (event) => {
        const select = event.target.closest('[data-product-set-variation-select]');

        if (!select) {
            return;
        }

        const option = select.options[select.selectedIndex];
        const item = select.closest('[data-product-set-item]');

        if (!option || !option.value) {
            selectVariation(item, '', '', '', null);
            return;
        }

        selectVariation(
            item,
            option.value,
            option.dataset.attributes,
            option.dataset.priceHtml,
            null,
        );
    });

    document.addEventListener('keydown', (event) => {
        if (!overlay || overlay.hidden) {
            return;
        }

        if (event.key === 'Escape') {
            event.preventDefault();

            if (closeSizeGuide()) {
                return;
            }

            closeDrawer();
            return;
        }

        if (event.key !== 'Tab') {
            return;
        }

        const focusableElements = getFocusableElements();

        if (!focusableElements.length) {
            event.preventDefault();
            overlay.focus();
            return;
        }

        const first = focusableElements[0];
        const last = focusableElements[focusableElements.length - 1];

        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    });

    document.querySelectorAll('.ProductSetShowcase').forEach((showcase) => {
        const viewport = showcase.querySelector('[data-product-set-showcase-viewport]');
        const pagination = showcase.querySelector('[data-product-set-showcase-pagination]');
        const previousButton = showcase.querySelector('[data-product-set-showcase-prev]');
        const nextButton = showcase.querySelector('[data-product-set-showcase-next]');
        const slides = showcase.querySelectorAll('.ProductSetShowcase__slide');

        if (!viewport || !slides.length || viewport.swiper) {
            return;
        }

        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const options = {
            modules: [A11y, Navigation, Pagination],
            slidesPerView: 'auto',
            spaceBetween: 16,
            slidesOffsetAfter: 20,
            speed: reduceMotion ? 0 : 420,
            allowTouchMove: true,
            simulateTouch: true,
            threshold: 6,
            touchRatio: 1,
            resistanceRatio: 0.72,
            preventClicks: true,
            preventClicksPropagation: true,
            watchOverflow: true,
            observer: true,
            observeParents: true,
            a11y: {
                enabled: true,
                containerMessage: 'Produkty tworzące set',
                slideLabelMessage: 'Produkt {{index}} z {{slidesLength}}',
                paginationBulletMessage: 'Przejdź do produktu {{index}}',
            },
            breakpoints: {
                1024: {
                    spaceBetween: 20,
                    slidesOffsetAfter: 0,
                },
            },
        };

        if (pagination && slides.length > 1) {
            options.pagination = {
                el: pagination,
                clickable: true,
                bulletClass: 'ProductSetShowcase__dot',
                bulletActiveClass: 'is-active',
                renderBullet: (index, className) =>
                    `<button class="Button--no-style ${className}" type="button" aria-label="Przejdź do produktu ${index + 1}"></button>`,
            };
        }

        if (previousButton && nextButton && slides.length > 1) {
            options.navigation = {
                prevEl: previousButton,
                nextEl: nextButton,
            };
        }

        new Swiper(viewport, options);
    });
}
